import { useEffect, useMemo, useRef, useState } from "react";
import {
  IconAlertTriangle,
  IconCheck,
  IconDownload,
  IconLoader2,
  IconPhoto,
  IconScanEye,
  IconSparkles,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";

import { getSession } from "../lib/auth";

const CV_API_BASE = (import.meta.env.VITE_CV_API_URL || "/cv-api").replace(/\/+$/, "");
const MAX_IMAGE_BYTES = 16 * 1024 * 1024;

type Mode =
  | "bunch_count"
  | "all"
  | "segment"
  | "drop"
  | "olc"
  | "ripeness"
  | "ffb_bbox"
  | "mesocarp"
  | "ffb_detached"
  | "ground_detached";

type CvRecord = Record<string, unknown>;
type ModelStatus = { loaded: boolean; path?: string; error?: string };
type RunResult = { mode: Mode; filename: string; data: CvRecord; completedAt: string };

const MODE_OPTIONS: Array<{ value: Mode; label: string; description: string }> = [
  {
    value: "bunch_count",
    label: "Oil palm bunch count",
    description: "Count and outline bunches with the dedicated segmentation model.",
  },
  {
    value: "all",
    label: "Full bunch inspection",
    description: "Run drop, colour, ripeness, bunch, mesocarp, socket and ground checks.",
  },
  {
    value: "segment",
    label: "Segment and grade each bunch",
    description: "Outline each bunch and classify its ripeness.",
  },
  { value: "drop", label: "Drop / keep classification", description: "Classify the bunch as drop or keep." },
  { value: "olc", label: "Outer layer colour (OLC)", description: "Classify the bunch outer colour." },
  { value: "ripeness", label: "Ripeness classification", description: "Classify ripeness from the image." },
  { value: "ffb_bbox", label: "Bunch object detection", description: "Detect and count bunch bounding boxes." },
  { value: "mesocarp", label: "Mesocarp colour detection", description: "Detect yellow, yellowish-orange and orange regions." },
  { value: "ffb_detached", label: "Detached socket detection", description: "Find detached sockets on the bunch." },
  { value: "ground_detached", label: "Ground fruit detection", description: "Find detached fruit on the ground." },
];

const MODEL_NAMES: Record<string, string> = {
  drop: "Drop / keep",
  olc: "Outer layer colour",
  ripeness: "Ripeness",
  ffb_det: "Bunch detector",
  ffb_bbox: "Bunch boxes",
  mesocarp: "Mesocarp",
  ffb_detached: "Detached sockets",
  ground_detached: "Ground fruit",
  segment: "Bunch segmentation",
  bunch_count: "Bunch counter",
};

function isRecord(value: unknown): value is CvRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function displayValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return "—";
}

async function cvRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getSession()?.token;
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${CV_API_BASE}${path}`, {
    ...init,
    headers,
  });
  const body = (await response.json().catch(() => ({}))) as {
    success?: boolean;
    message?: string;
  };
  if (!response.ok || body.success === false) {
    throw new Error(body.message || `Computer-vision service returned ${response.status}.`);
  }
  return body as T;
}

export function BunchVisionWorkspace() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("bunch_count");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [models, setModels] = useState<Record<string, ModelStatus> | null>(null);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [annotatedImageUrl, setAnnotatedImageUrl] = useState<string | null>(null);

  const selectedMode = MODE_OPTIONS.find((option) => option.value === mode)!;
  const loadedModels = useMemo(
    () => Object.values(models ?? {}).filter((model) => model.loaded).length,
    [models],
  );
  const modelTotal = Object.keys(models ?? {}).length;
  const serviceReady = !serviceError && modelTotal > 0 && loadedModels === modelTotal;
  const serviceLabel = serviceError
    ? "Vision service unavailable"
    : models
      ? `${loadedModels}/${modelTotal} models loaded`
      : "Connecting to vision service";

  useEffect(() => {
    let active = true;
    cvRequest<{ models?: Record<string, ModelStatus> }>("/api/models")
      .then((response) => {
        if (active) {
          setModels(response.models ?? {});
          setServiceError(null);
        }
      })
      .catch((cause: unknown) => {
        if (active) setServiceError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const annotatedPath = result?.data.processed_image_url;
  useEffect(() => {
    if (typeof annotatedPath !== "string") {
      setAnnotatedImageUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    const controller = new AbortController();
    const token = getSession()?.token;
    fetch(`${CV_API_BASE}${annotatedPath}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load the annotated image.");
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setAnnotatedImageUrl(objectUrl);
      })
      .catch(() => setAnnotatedImageUrl(null));
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [annotatedPath]);

  const chooseFile = (candidate?: File) => {
    if (!candidate) return;
    if (!candidate.type.startsWith("image/")) {
      setError("Choose an image file (JPG, PNG, WEBP, GIF or BMP).");
      return;
    }
    if (candidate.size > MAX_IMAGE_BYTES) {
      setError("This image is larger than the 16 MB upload limit.");
      return;
    }
    setError(null);
    setResult(null);
    setFile(candidate);
  };

  const clearImage = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const runInspection = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const uploaded = await cvRequest<{ filename?: string }>("/api/upload", {
        method: "POST",
        body: form,
      });
      if (!uploaded.filename) throw new Error("The service did not return the uploaded image name.");

      const endpoint =
        mode === "bunch_count"
          ? "/api/count-bunches"
          : mode === "all"
            ? "/api/detect"
            : mode === "segment"
              ? "/api/segment"
              : `/api/detect/${mode}`;
      const response = await cvRequest<{ result?: CvRecord }>(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: uploaded.filename }),
      });
      if (!response.result) throw new Error("The service returned no detection results.");
      setResult({ mode, filename: file.name, data: response.result, completedAt: new Date().toISOString() });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const downloadResult = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ffb-inspection-${new Date(result.completedAt).toISOString().replace(/[:.]/g, "-")}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const resultImage = annotatedImageUrl ?? previewUrl;

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface-2 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-bg-accent text-text-accent">
            <IconScanEye size={19} />
          </span>
          <div>
            <h2 className="font-semibold">Oil Palm Bunch Vision</h2>
            <p className="text-xs text-text-secondary">Upload a photo to count, segment and inspect fresh fruit bunches.</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${serviceReady ? "border-border-success bg-bg-success text-text-success" : "border-border-warning bg-bg-warning text-text-warning"}`}>
          {serviceReady ? <IconCheck size={13} /> : <IconAlertTriangle size={13} />}
          {serviceLabel}
        </div>
      </div>

      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(290px,0.82fr)]">
        <div className="space-y-3">
          <label className="block space-y-1.5 text-xs">
            <span className="font-medium text-text-secondary">Inspection mode</span>
            <select
              value={mode}
              onChange={(event) => setMode(event.target.value as Mode)}
              className="w-full rounded-lg border border-border-strong bg-surface-2 px-3 py-2 text-sm"
            >
              {MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <span className="block text-[11px] text-text-muted">{selectedMode.description}</span>
          </label>

          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
            className="hidden"
            onChange={(event) => chooseFile(event.currentTarget.files?.[0])}
          />

          {file && previewUrl ? (
            <div className="overflow-hidden rounded-xl border border-border">
              <div className="flex items-center justify-between gap-2 border-b border-border bg-surface-1 px-3 py-2 text-xs">
                <span className="flex min-w-0 items-center gap-2 truncate text-text-secondary">
                  <IconPhoto size={15} className="shrink-0" />
                  <span className="truncate">{file.name}</span>
                  <span className="shrink-0 text-text-muted">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
                </span>
                <button type="button" onClick={clearImage} className="rounded p-1 text-text-secondary hover:bg-surface-2" aria-label="Remove image">
                  <IconX size={15} />
                </button>
              </div>
              <div className="grid min-h-56 place-items-center bg-[#111714] p-2">
                <img src={previewUrl} alt="Selected oil palm bunches" className="max-h-[420px] max-w-full object-contain" />
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => { event.preventDefault(); setDragging(false); chooseFile(event.dataTransfer.files?.[0]); }}
              className={`flex min-h-56 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-5 text-center transition-colors ${dragging ? "border-text-accent bg-bg-accent" : "border-border-strong bg-surface-1 hover:border-border-accent hover:bg-bg-accent/40"}`}
            >
              <IconUpload size={24} className="text-text-accent" />
              <span className="text-sm font-semibold">Drop an image here or browse</span>
              <span className="text-xs text-text-secondary">JPG, PNG, WEBP, GIF or BMP · up to 16 MB</span>
            </button>
          )}

          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-border-danger bg-bg-danger px-3 py-2 text-xs text-text-danger">
              <IconAlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {(serviceError || (models && !serviceReady)) && (
            <p className="text-[11px] text-text-secondary">
              {serviceError
                ? <>Start the Python service in <code>apps/ffb-cv-service</code> to enable image analysis. {serviceError}</>
                : "Some model files did not load. The available inspections may be limited; check the Python service logs."}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={runInspection} disabled={!file || busy} className="btn-primary">
              {busy ? <IconLoader2 size={15} className="animate-spin" /> : <IconSparkles size={15} />}
              {busy ? "Inspecting image…" : "Run inspection"}
            </button>
            <button type="button" onClick={clearImage} disabled={!file || busy} className="btn-ghost">
              <IconTrash size={14} /> Clear
            </button>
            {result && (
              <button type="button" onClick={downloadResult} className="btn-ghost sm:ml-auto">
                <IconDownload size={14} /> Download JSON
              </button>
            )}
          </div>
        </div>

        <div className="min-w-0 space-y-3">
          {result ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{MODE_OPTIONS.find((option) => option.value === result.mode)?.label}</p>
                  <p className="text-[11px] text-text-secondary">{result.filename} · {new Date(result.completedAt).toLocaleString()}</p>
                </div>
                <span className="rounded-full bg-bg-success px-2 py-1 text-[10px] font-semibold text-text-success">Complete</span>
              </div>
              {resultImage && (
                <div className="overflow-hidden rounded-lg border border-border bg-[#111714]">
                  <img src={resultImage} alt="Bunch detection result" className="max-h-[360px] w-full object-contain" />
                </div>
              )}
              <ResultSummary mode={result.mode} data={result.data} />
              <details className="rounded-lg border border-border bg-surface-1 px-3 py-2">
                <summary className="cursor-pointer text-xs font-medium">Full model response</summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-[10px] text-text-secondary">{JSON.stringify(result.data, null, 2)}</pre>
              </details>
            </>
          ) : (
            <div className="flex min-h-56 flex-col justify-center rounded-xl border border-border bg-surface-1 p-4">
              <span className="mb-3 grid h-10 w-10 place-items-center rounded-full bg-surface-2 text-text-accent"><IconSparkles size={19} /></span>
              <h3 className="text-sm font-semibold">Choose an inspection and add a photo</h3>
              <p className="mt-1 text-xs text-text-secondary">Results include detected counts, confidence scores, ripeness classes, and annotated masks when that model produces an overlay.</p>
              {models && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {Object.entries(models).map(([name, status]) => (
                    <span key={name} className={`rounded-full border px-2 py-1 text-[10px] ${status.loaded ? "border-border-success bg-bg-success text-text-success" : "border-border bg-surface-2 text-text-muted"}`}>
                      {status.loaded ? "✓" : "·"} {MODEL_NAMES[name] ?? name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ResultSummary({ mode, data }: { mode: Mode; data: CvRecord }) {
  if (mode === "all") {
    const models = Object.entries(data).filter(([, value]) => isRecord(value));
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {models.map(([name, value]) => (
          <div key={name} className="space-y-2">
            <ResultCard name={name} value={value as CvRecord} />
            <DetectionRows items={(value as CvRecord).detections} />
          </div>
        ))}
      </div>
    );
  }
  if (mode === "segment") {
    const statistics = isRecord(data.statistics) ? data.statistics : {};
    return (
      <div className="space-y-2">
        <div className="rounded-lg border border-border bg-surface-1 p-3">
          <p className="text-xs font-semibold">{displayValue(data.total_ffb)} bunches segmented</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(statistics).map(([label, count]) => (
              <span key={label} className="rounded-full border border-border bg-surface-2 px-2 py-1 text-[10px]">{label}: {displayValue(count)}</span>
            ))}
          </div>
        </div>
        <DetectionRows items={data.segments} />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <ResultCard name={mode} value={data} />
      <DetectionRows items={data.detections} />
    </div>
  );
}

function DetectionRows({ items }: { items: unknown }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const rows = items.filter(isRecord);
  if (rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-surface-1 p-3">
      <p className="mb-2 text-[11px] font-semibold">Detected items · {rows.length}</p>
      <div className="max-h-44 space-y-1 overflow-auto">
        {rows.slice(0, 40).map((item, index) => {
          const label = item.ripeness ?? item.class_name ?? item.label ?? item.tag ?? "Bunch";
          const confidence = typeof item.confidence === "number" ? `${Math.round(item.confidence * 100)}%` : null;
          const bbox = Array.isArray(item.bbox) ? item.bbox.map(displayValue).join(", ") : null;
          return (
            <div key={String(item.id ?? index)} className="flex items-center justify-between gap-2 border-b border-border py-1 text-[10px] last:border-0">
              <span className="min-w-0 truncate">#{displayValue(item.id ?? index + 1)} · {displayValue(label)}</span>
              <span className="shrink-0 text-text-secondary">{confidence ?? ""}{bbox ? ` · [${bbox}]` : ""}</span>
            </div>
          );
        })}
        {rows.length > 40 && <p className="pt-1 text-[10px] text-text-muted">Showing the first 40 of {rows.length} items. Download the JSON for all results.</p>}
      </div>
    </div>
  );
}

function ResultCard({ name, value }: { name: string; value: CvRecord }) {
  const count = typeof value.count === "number" ? value.count : undefined;
  const bunches = typeof value.total_bunches === "number" ? value.total_bunches : undefined;
  const label = typeof value.class === "string" ? value.class : null;
  const confidence = typeof value.confidence === "number" ? `${Math.round(value.confidence * 100)}% confidence` : null;
  const message = typeof value.error === "string" ? value.error : null;
  return (
    <div className="rounded-lg border border-border bg-surface-1 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold">{MODEL_NAMES[name] ?? name}</p>
        {message ? <IconAlertTriangle size={14} className="text-text-warning" /> : <IconCheck size={14} className="text-text-success" />}
      </div>
      {message ? (
        <p className="mt-1 text-[11px] text-text-warning">{message}</p>
      ) : (
        <p className="mt-1 text-sm font-semibold">
          {bunches !== undefined ? `${bunches} bunches` : count !== undefined ? `${count} detections` : label ?? "Analysis complete"}
        </p>
      )}
      {(label && count !== undefined) && <p className="text-[11px] text-text-secondary">{label}</p>}
      {confidence && <p className="text-[10px] text-text-muted">{confidence}</p>}
      {isRecord(value.probs) && (
        <div className="mt-2 space-y-1">
          {Object.entries(value.probs).map(([className, probability]) => (
            <div key={className} className="flex items-center justify-between gap-2 text-[10px] text-text-secondary">
              <span className="truncate">{className}</span><span>{typeof probability === "number" ? `${Math.round(probability * 100)}%` : displayValue(probability)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
